import { useState, useRef, useCallback, useEffect } from "react";
import { chatService } from "./ChatService";
import { FILE_CONFIG } from "./saanvi-constants";

export default function useChatLogic(
  authToken,
  initialPrompt,
  showInitialPrompt,
  isAuthenticated
) {
  const [chat, setChat] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Thinking about it...");
  const [inCommingMessage, setInCommingMessage] = useState("");
  const [analysisMode, setAnalysisMode] = useState(null); // "deep_analysis", "deep_dive", "deeper_dive", "thinking_mode", or null
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [analysisStartTime, setAnalysisStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState("");
  const [initialPromptProcessed, setInitialPromptProcessed] = useState(false);
  const [progressUpdates, setProgressUpdates] = useState([]);
  const thinkingMode = analysisMode === "thinking_mode";

  // Derived values for backward compatibility
  const deepAnalysis = analysisMode === "deep_analysis";
  const deepDive = analysisMode === "deep_dive";
  const deeperDive = analysisMode === "deeper_dive";

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Elapsed time counter
  useEffect(() => {
    let interval;
    if (
      analysisStartTime &&
      (deepAnalysis || deepDive || deeperDive || thinkingMode)
    ) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - analysisStartTime) / 1000);
        const minutes = Math.floor(elapsed / 60);
        const seconds = elapsed % 60;
        setElapsedTime(minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [analysisStartTime, deepAnalysis, deepDive, deeperDive, thinkingMode]);

  // Auto-scroll
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chat, inCommingMessage, scrollToBottom]);

  // Initial prompt handling
  useEffect(() => {
    if (initialPrompt && !initialPromptProcessed) {
      setInitialPromptProcessed(true);
      if (showInitialPrompt) {
        setMessage(initialPrompt);
      } else {
        handleSendMessage(null, initialPrompt);
      }
    }
  }, [initialPrompt, showInitialPrompt, initialPromptProcessed]);

  // Progress update callback
  const handleProgressUpdate = useCallback((progressData) => {
    setProgressUpdates((prev) => [...prev, progressData]);
  }, []);

  const handleSendMessage = useCallback(
    async (e, customMessage = null) => {
      e?.preventDefault();

      const messageToSend = customMessage || message;
      if (loading || (!messageToSend.trim() && selectedFiles.length === 0))
        return;

      const limitExceeded =
        !!localStorage.getItem("SAANVI-LIMIT-EXCEEDED") && !isAuthenticated;
      if (limitExceeded) return;

      const newChat = [
        ...chat,
        {
          role: "user",
          content: messageToSend.trim() || "Analyze these files",
          files: selectedFiles.length > 0 ? selectedFiles : undefined,
        },
      ];
      setChat(newChat);
      setMessage("");
      setProgressUpdates([]);

      if (deepAnalysis || deepDive || deeperDive || thinkingMode) {
        setAnalysisStartTime(Date.now());
      }

      const filesToSend = [...selectedFiles];
      setSelectedFiles([]);

      await chatService.sendStreamedMessage({
        chat: newChat,
        deepAnalysis,
        deepDive,
        deeperDive,
        thinkingMode, // NEW
        files: filesToSend,
        addChat: (newMessage) => {
          setChat((prev) => [...prev, newMessage]);
          setAnalysisStartTime(null);
          setProgressUpdates([]);
        },
        setLoading,
        setLoadingMessage,
        setInCommingMessage,
        scrollToBottom,
        showError: (msg) => {
          throw new Error(msg);
        },
        authToken,
        onProgressUpdate: handleProgressUpdate,
      });

      scrollToBottom();
    },
    [
      loading,
      message,
      chat,
      deepAnalysis,
      deepDive,
      deeperDive,
      thinkingMode, // NEW
      selectedFiles,
      isAuthenticated,
      authToken,
      scrollToBottom,
      handleProgressUpdate,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage(e);
      }
    },
    [handleSendMessage]
  );

  const handleFileSelect = useCallback(
    (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;

      if (selectedFiles.length + files.length > FILE_CONFIG.maxFiles) {
        throw new Error(`Maximum ${FILE_CONFIG.maxFiles} files allowed`);
      }

      const invalidFile = files.find(
        (file) => !FILE_CONFIG.allowedTypes.includes(file.type)
      );
      if (invalidFile) {
        throw new Error(`Invalid file type: ${invalidFile.name}`);
      }

      const oversizedFile = files.find(
        (file) => file.size > FILE_CONFIG.maxFileSize
      );
      if (oversizedFile) {
        throw new Error(
          `File too large: ${oversizedFile.name}. Maximum size is 20MB.`
        );
      }

      const filesWithPreviews = files.map((file) => {
        if (file.type.startsWith("image/")) {
          return Object.assign(file, { preview: URL.createObjectURL(file) });
        }
        return file;
      });

      setSelectedFiles((prev) => [...prev, ...filesWithPreviews]);

      if (e.target) {
        e.target.value = "";
      }
    },
    [selectedFiles]
  );

  const handleRemoveFile = useCallback((index) => {
    setSelectedFiles((prev) => {
      const file = prev[index];
      if (file && file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const stopGeneration = useCallback(() => {
    chatService.cancelCurrentRequest();
    setLoading(false);
    setLoadingMessage("Thinking about it...");
    setAnalysisStartTime(null);
    setProgressUpdates([]);
  }, []);

  const clearChat = useCallback(() => {
    chatService.cancelCurrentRequest();
    setChat([]);
    setLoading(false);
    setInCommingMessage("");
    setLoadingMessage("Thinking about it...");
    setAnalysisStartTime(null);
    setInitialPromptProcessed(false);
    setProgressUpdates([]);

    selectedFiles.forEach((file) => {
      if (file.preview) URL.revokeObjectURL(file.preview);
    });

    setSelectedFiles([]);
  }, [selectedFiles]);

  const handleDeepAnalysisToggle = useCallback(() => {
    setAnalysisMode((prev) =>
      prev === "deep_analysis" ? null : "deep_analysis"
    );
  }, []);

  const handleDeepDiveToggle = useCallback(() => {
    setAnalysisMode((prev) => (prev === "deep_dive" ? null : "deep_dive"));
  }, []);

  const handleDeeperDiveToggle = useCallback(() => {
    setAnalysisMode((prev) => (prev === "deeper_dive" ? null : "deeper_dive"));
  }, []);

  const handleThinkingModeToggle = useCallback(() => {
    setAnalysisMode((prev) => {
      if (prev === "thinking_mode") {
        return null; // Turn off thinking mode
      }
      return "thinking_mode"; // Turn on thinking mode (turns off others)
    });
  }, []);

  return {
    chat,
    message,
    setMessage,
    loading,
    loadingMessage,
    inCommingMessage,
    deepAnalysis,
    deepDive,
    deeperDive,
    thinkingMode,
    selectedFiles,
    elapsedTime,
    progressUpdates,
    scrollRef,
    fileInputRef,
    messagesEndRef,
    handleSendMessage,
    handleKeyDown,
    handleFileSelect,
    handleRemoveFile,
    stopGeneration,
    clearChat,
    handleDeepAnalysisToggle,
    handleDeepDiveToggle,
    handleDeeperDiveToggle,
    handleThinkingModeToggle,
    scrollToBottom,
  };
}
